import { useEffect, useState, useCallback } from "react";
import { Avatar, Card, CircularProgress } from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";
import { fetchAllPlayers } from "api/quizGame";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import ActualDietPlanLoader from "@components/ui/Loaders/ActualDietPlanLoader";
import { useUserData } from "@context/OAuth/AuthContext";
import {
  FirstRank,
  SecondRank,
  ThirdRank,
  BackImg,
  StarRank1,
  StarRank2,
  StarRank3,
  YellowRanks,
  GreenRank,
  GrayRank,
  BrownRanks,
  Whitebluestar,
  BlueBox,
} from "@utils/defaultImages";
import PodiumCard from "./PodiumCard";
import NoHistory from "@features/mindfulness/NoHistory";

const WeeklyLeaderBoard = () => {
  const { user } = useUserData();
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [nextPage, setNextPage] = useState(2); // Start from page 2 since we already loaded page 1
  const [hasMore, setHasMore] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [error, setError] = useState(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const Ranks = [<YellowRanks />, <GrayRank />, <BrownRanks />, <GreenRank />];

  // Detect mobile screen
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const fetchLeaderboardData = useCallback(
    async (page = 1, isNewLoad = false) => {
      try {
        const response = await fetchAllPlayers({ page, per_page: 10 });

        if (response) {
          // Extract data and pagination info from response
          const {
            data: players,
            current_page,
            last_page,
            total,
          } = response?.top_players_this_week || {};

          // Check if we have valid data
          if (!players) {
            setHasMore(false);
            setIsInitialLoad(false);
            return;
          }

          setCurrentUser(response?.current_user); 
          if (isNewLoad || page === 1) {
            setLeaderboardData(players);
          } else {
            setLeaderboardData((prev) => [...prev, ...players]);
          }

          // Update pagination state
          const hasMorePages = current_page < last_page;
          setHasMore(hasMorePages);

          // Set next page to load
          if (hasMorePages) {
            setNextPage(current_page + 1);
          } else {
            setNextPage(null);
          }
        }

        setIsInitialLoad(false);
      } catch (error) {
        console.error("Failed to get leaderboard data:", error);
        setError(error.response?.data?.message || "An error occurred");
        setHasMore(false);
        setIsInitialLoad(false);
      }
    },
    []
  );

  // Initial fetch
  useEffect(() => {
    setNextPage(2); 
    setHasMore(true);
    setLeaderboardData([]);
    fetchLeaderboardData(1, true);
  }, [fetchLeaderboardData]);

  const fetchMoreData = () => {
    if (hasMore && nextPage) {
      fetchLeaderboardData(nextPage, false);
    }
  };

  const topThree = leaderboardData?.slice(0, 3) || [];

  const otherPlayers = isMobile
    ? leaderboardData || [] 
    : leaderboardData?.slice(3) || []; 

  if (isInitialLoad) {
    return <ActualDietPlanLoader quizGame={true} />;
  }

  return (
    <div className="min-h-screen">
      {/* Return button */}
      <div className="flex justify-end p-4">
        <button
          className="bg-[#64DDFF] py-[12px] px-6 text-white rounded-full  mb-2 flex items-center gap-2"
          onClick={() => navigate("/quiz-game")}
        >
          <ChevronLeft
            className="w-6 h-6 bg-white rounded-full text-[#4CDBFF] p-[2px]"
            strokeWidth={4}
          />
          {t("quizGame.Return To Dashboard")}
        </button>
      </div>

      {/* Main leaderboard container */}
      <div
        className="p-6 md:p-8 rounded-2xl bg-cover h-full"
        style={{ backgroundImage: `url(${BackImg})` }}
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <h2 className="text-[16px] font-[500] text-white font-[Lato]">
            {t("quizGame.Weekly Leaderboard")}
          </h2>
        </div>

        {/* Podium section - Desktop only */}
        {!isMobile && !isInitialLoad && leaderboardData.length > 0 && (
          <div className="flex justify-center items-end gap-2 md:gap-2 mb-8 px-4">
            {topThree?.length >= 3 && (
              <>
                {/* 2nd place */}
                <PodiumCard
                  gradient="linear-gradient(124.8deg, #E5E5E5 1.6%, #A9B6BF 70.64%)"
                  name={topThree[1]?.name}
                  points={topThree[1]?.points}
                  profile_url={topThree[1]?.profile_url}
                  RankImage={<SecondRank />}
                  StarRank={<StarRank2 />}
                  blueDiv={
                    <div className="relative">
                      <BlueBox className="h-[50px] md:h-[68px] w-[150px] md:w-[170px]" />
                      <div
                        className="
                            absolute
                            top-1/2
                            left-1/2
                            -translate-x-1/2
                            -translate-y-1/2
                            bg-white
                            rounded-full
                            w-10
                            h-10
                            text-[#50DCFF]
                            flex
                            items-center
                            justify-center
                             "
                      >
                        2nd
                      </div>
                    </div>
                  }
                />

                {/* 1st place */}
                <PodiumCard
                  gradient="linear-gradient(124.8deg, #FAE571 1.6%, #F0A538 70.64%)"
                  name={topThree[0]?.name}
                  points={topThree[0]?.points}
                  profile_url={topThree[0]?.profile_url}
                  RankImage={<FirstRank />}
                  StarRank={<StarRank1 />}
                  blueDiv={
                    <div className="relative">
                      <BlueBox className="h-[70px] md:h-[105px]  w-[180px] md:w-[200px]" />
                      <div
                        className="
                            absolute
                            top-1/2
                            left-1/2
                            -translate-x-1/2
                            -translate-y-1/2
                            bg-white
                            rounded-full
                            w-10
                            h-10
                            text-[#50DCFF]
                            flex
                            items-center
                            justify-center
                             "
                      >
                        1st
                      </div>
                    </div>
                  }
                />
                {/* 3rd place */}
                <PodiumCard
                  gradient="linear-gradient(124.8deg, #ECD396 1.6%, #DC965D 70.64%)"
                  name={topThree[2]?.name}
                  points={topThree[2]?.points}
                  profile_url={topThree[2]?.profile_url}
                  RankImage={<ThirdRank />}
                  StarRank={<StarRank3 />}
                  blueDiv={
                    <div className="relative">
                      <BlueBox className="h-[50px] md:h-[68px] w-[150px] md:w-[170px]" />

                      <div
                        className="
                            absolute
                            top-1/2
                            left-1/2
                            -translate-x-1/2
                            -translate-y-1/2
                            bg-white
                            rounded-full
                            w-10
                            h-10
                            text-[#50DCFF]
                            flex
                            items-center
                            justify-center
                             "
                      >
                        3rd
                      </div>
                    </div>
                  }
                />
              </>
            )}
          </div>
        )}

        {/* Current user section */}
        {currentUser && (
          <div className="mb-6 flex justify-center w-full">
            <div className="w-full md:max-w-[80%] bg-white rounded-full p-2 md:p-2 flex items-center gap-3 shadow-md">
              <div className="relative">
                <Avatar
                  className="!w-8 !h-8 md:!w-10 md:!h-10"
                  src={currentUser.avatar || currentUser.profile_url}
                  alt={currentUser.name}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800 truncate text-sm md:text-base">
                  {t("You")}
                </p>
              </div>
              <div className="relative">
                {Ranks[Math.min(currentUser.rank, 3)]}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[14px] font-[Lato] text-white">
                  {currentUser.rank}
                </div>
              </div>

              <div className="bg-[#4CDBFF] rounded-full px-2 py-1 flex items-center gap-2">
                <div className="rounded-full bg-white p-1">
                  <Whitebluestar />
                </div>
                <p className="text-[#fff] text-[14px] font-[Lato] font-[600]">
                  {currentUser.points || 0} {t("Points")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Players list with infinite scroll */}
        {leaderboardData?.length > 0 ? (
          <div className="rounded-xl overflow-hidden">
            {/* Scrollable data container */}
            <div
              id="scrollable-leaderboard-container"
              className="max-h-[400px] overflow-y-auto pr-2 custom-scrollbar"
            >
              <InfiniteScroll
                dataLength={otherPlayers.length}
                next={fetchMoreData}
                hasMore={hasMore}
                loader={
                  hasMore ? (
                    <div className="text-center py-4">
                      <CircularProgress
                            sx={{ color: "#fff" }}

                        thickness={7}
                        size={24}
                      />
                    </div>
                  ) : null
                }
                endMessage={
                  otherPlayers.length > 0 ? (
                    <div className="text-center py-4 text-gray-500 text-sm text-white">
                      All players loaded
                    </div>
                  ) : null
                }
                scrollableTarget="scrollable-leaderboard-container"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 p-4">
                  {otherPlayers.map((player, index) => {
                    // Calculate rank based on mobile/desktop
                    let rank;
                    if (isMobile) {
                      // On mobile, use the actual rank from player data or calculate from index
                      rank = player.rank || index + 1;
                    } else {
                      // On desktop, since top 3 are displayed separately, rank starts from 4
                      rank = player.rank || index + 4;
                    }

                    return (
                      <div
                        key={player.id || index}
                        className={`relative bg-[#D2FCFF] rounded-xl p-3 md:p-4 transition-all duration-200 hover:shadow-md hover:scale-[1.02] ${
                          player.user_id === user?.id || player.is_current_user
                            ? "bg-gradient-to-r from-[#0771EF]/10 to-[#15AAFC]/10 border border-[#0771EF]/30"
                            : ""
                        }`}
                      >
                        {/* Regular rank badge */}
                        <div className="absolute top-0 right-3">
                          <div className="relative">
                            {Ranks[Math.min(player?.rank || rank, 3)]}
                            <div className="absolute top-2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[14px] font-[Lato] text-white">
                              {player?.rank || rank}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <div className="relative">
                            <Avatar
                              className="!w-12 !h-12 md:!w-[70px] md:!h-[70px]"
                              src={player.avatar || player.profile_url}
                              alt={player.name}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-800 truncate text-sm md:text-base">
                              {player.name || "Unknown Player"}
                            </p>
                          </div>
                        </div>
                        <div className="flex justify-end">
                          <div className="mt-1 bg-[#4CDBFF] rounded-full px-2 py-1 flex items-center gap-2 w-[fit-content]">
                            <div className="rounded-full bg-white p-1">
                              <Whitebluestar />
                            </div>
                            <p className="text-[#fff] text-[14px] font-[Lato] font-[600]">
                              {player?.points || 0} Points
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </InfiniteScroll>
            </div>
          </div>
        ) : !isInitialLoad ? (
          <div className="text-white text-center py-4">
            <NoHistory topPlayers={true} />
          </div>
        ) : null}

        <style jsx>{`
          @keyframes slide-up {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes fade-in {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          .animate-slide-up {
            animation: slide-up 0.5s ease-out forwards;
          }

          .animate-fade-in {
            animation: fade-in 0.3s ease-out forwards;
          }

          .animation-delay-100 {
            animation-delay: 100ms;
          }

          .animation-delay-200 {
            animation-delay: 200ms;
          }

          .animation-delay-300 {
            animation-delay: 300ms;
          }

          .custom-scrollbar::-webkit-scrollbar {
            width: 15px;
          }

          .custom-scrollbar::-webkit-scrollbar-track {
            background-color: #f1f1f1;
            border-radius: 10px;
            margin: 4px 0;
          }

          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: linear-gradient(150deg, #0771ef 12.89%, #15aafc 85.22%);
            border-radius: 10px;
            border: 1px solid rgba(255, 255, 255, 0.2);
          }

          @media (max-width: 640px) {
            .custom-scrollbar::-webkit-scrollbar {
              width: 4px;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default WeeklyLeaderBoard;
